import { Button, ButtonGroup, Checkbox, Menu, MenuItem, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { DEFAULT_LANGUAGE, LANGUAGE_OPTIONS } from '../../../../../../../utils/i18n';
import { useCallback, useState } from 'react';
import omit from 'lodash/omit';
import { useFormikContext } from 'formik';
import { UpdateConfigDialogFormValues } from '../../dto';

type LanguageSwitcherProps = {
    languagesUsed: string[];
    currentLanguage: string;
    onChangeLanguage: (newLanguage: string) => void;
};

export const LanguageSwitcher = ({ languagesUsed, currentLanguage, onChangeLanguage }: LanguageSwitcherProps) => {
    const [addLanguageMenuAnchorElement, setAddLanguageMenuAnchorElement] = useState<HTMLButtonElement | null>(null);
    const { values, isSubmitting, setFieldValue } = useFormikContext<UpdateConfigDialogFormValues>();

    const toggleLanguageSupport = useCallback(
        (langCode: string) => {
            if (languagesUsed.includes(langCode)) {
                setFieldValue(
                    'config.columns',
                    values.config.columns.map((column) => ({
                        ...column,
                        columnTitle: column.columnTitle ? omit(column.columnTitle, langCode) : column.columnTitle,
                    })),
                );

                setFieldValue(
                    'config.filters.customFilters',
                    values.config.filters.customFilters.map((filter) => ({
                        ...filter,
                        label: filter.label ? omit(filter.label, langCode) : filter.label,
                    })),
                );

                if (currentLanguage === langCode) {
                    onChangeLanguage(DEFAULT_LANGUAGE);
                }
                return;
            }

            // Add language support
            setFieldValue(
                'config.columns',
                values.config.columns.map((column) => ({
                    ...column,
                    columnTitle: {
                        ...(column.columnTitle ?? { [DEFAULT_LANGUAGE]: column.title }),
                        [langCode]: column.columnTitle?.[DEFAULT_LANGUAGE] ?? column.title,
                    },
                })),
            );

            setFieldValue(
                'config.filters.customFilters',
                values.config.filters.customFilters.map((filter) => {
                    const defaultLabel = filter.label?.[DEFAULT_LANGUAGE] ?? '';
                    const defaultAllOptionsLabel = filter.allOptionsLabel?.[DEFAULT_LANGUAGE] ?? '';

                    return {
                        ...filter,
                        label: {
                            ...(filter.label ?? { [DEFAULT_LANGUAGE]: defaultLabel }),
                            [langCode]: filter.label[DEFAULT_LANGUAGE],
                        },
                        ...(filter.allOptionsLabel && {
                            allOptionsLabel: {
                                ...(filter.allOptionsLabel ?? { [DEFAULT_LANGUAGE]: defaultAllOptionsLabel }),
                                [langCode]: filter.allOptionsLabel?.[DEFAULT_LANGUAGE] ?? '',
                            },
                        }),
                    };
                }),
            );
        },
        [
            languagesUsed,
            setFieldValue,
            values.config.columns,
            values.config.filters.customFilters,
            currentLanguage,
            onChangeLanguage,
        ],
    );

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <span>
                <FormattedMessage id="inside-fifa.generic-tables.language-switcher-label" />
            </span>
            <ButtonGroup>
                {languagesUsed.map((langCode, index) => (
                    <Button
                        key={index}
                        variant={langCode === currentLanguage ? 'contained' : 'outlined'}
                        onClick={() => onChangeLanguage(langCode)}
                    >
                        <FormattedMessage id={LANGUAGE_OPTIONS.find(({ code }) => code === langCode)!.translationKey} />
                    </Button>
                ))}
            </ButtonGroup>
            <Button
                variant="contained"
                color="info"
                onClick={(event) => {
                    setAddLanguageMenuAnchorElement(event.currentTarget);
                }}
                disabled={isSubmitting}
            >
                <FormattedMessage id="inside-fifa.generic-tables.toggle-supported-languages" />
            </Button>
            <Menu
                open={Boolean(addLanguageMenuAnchorElement)}
                anchorEl={addLanguageMenuAnchorElement}
                onClose={() => setAddLanguageMenuAnchorElement(null)}
            >
                {LANGUAGE_OPTIONS.map((option, index) => (
                    <MenuItem
                        key={index}
                        disabled={option.code === DEFAULT_LANGUAGE}
                        onClick={() => {
                            setAddLanguageMenuAnchorElement(null);
                            toggleLanguageSupport(option.code);
                        }}
                    >
                        <Checkbox checked={languagesUsed.includes(option.code)} />
                        <FormattedMessage id={option.translationKey} />
                    </MenuItem>
                ))}
            </Menu>
        </Stack>
    );
};
